import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import { Gavel } from "lucide-react";
import Seo, { breadcrumbLd } from "../components/Seo";

const resolveImg = (url) =>
  url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;

function useCountdown(endIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const end = new Date(endIso).getTime();
  const diff = Math.max(0, end - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, ended: diff === 0 };
}

const formatCountdown = (days, hours, minutes, seconds) => {
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

const auctionStatusLabel = (auction, isLive) => {
  if (isLive) return "Live";
  if (auction.status === "sold") return "Sold";
  return "Ended";
};

const AuctionCard = ({ auction }) => {
  const { days, hours, minutes, seconds, ended } = useCountdown(auction.end_time);
  const isLive = auction.status === "live" && !ended;
  return (
    <Link
      to={`/auction/${auction.id}`}
      data-testid={`auction-card-${auction.id}`}
      className="block group border border-[#E5E0D8] hover:border-[#1E3A8A] bg-white transition-colors"
    >
      <div className="relative aspect-[4/5] bg-[#F2EDE5] overflow-hidden">
        <img
          src={resolveImg(auction.artwork_image)}
          alt={`${auction.artwork_title} — live African art auction lot${auction.artist_name ? ` by ${auction.artist_name}` : ""}`}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 flex items-center gap-2 label-eyebrow bg-[#1E3A8A] text-[#F4C430] px-2.5 py-1">
          <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-[#F4C430] animate-pulse" : "bg-white/60"}`} />
          {auctionStatusLabel(auction, isLive)}
        </div>
      </div>
      <div className="p-5">
        <p className="label-eyebrow text-[#1E3A8A]">Auction · {auction.artist_name}</p>
        <h3 className="font-serif-display text-2xl text-[#111] mt-2 font-medium leading-tight">
          {auction.artwork_title}
        </h3>
        <div className="mt-4 grid grid-cols-2 gap-3 border-t border-[#E5E0D8] pt-4">
          <div>
            <p className="label-eyebrow text-[#555]">Current bid</p>
            <p className="font-mono-aam text-base text-[#1E3A8A] mt-1">
              ${(auction.current_bid || auction.starting_price).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="label-eyebrow text-[#555]">{isLive ? "Ends in" : "Status"}</p>
            <p className="font-mono-aam text-base text-[#111] mt-1">
              {isLive ? formatCountdown(days, hours, minutes, seconds) : auction.status}
            </p>
          </div>
        </div>
        <p className="label-eyebrow text-[#555] mt-3">{auction.bid_count} bid{auction.bid_count === 1 ? "" : "s"}</p>
      </div>
    </Link>
  );
};

const TABS = [
  { key: "live", label: "Live now" },
  { key: "ended", label: "Ended" },
  { key: "sold", label: "Recently sold" },
];

export default function Auctions() {
  const [tab, setTab] = useState("live");
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/auctions", { params: { status: tab } })
      .then(({ data }) => setAuctions(data))
      .catch(() => {
        /* silent failure — empty list is acceptable */
      })
      .finally(() => setLoading(false));
  }, [tab]);

  return (
    <div data-testid="auctions-page" className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
      <Seo
        title="Live Auctions — African Contemporary Art"
        description="Bid live on original works by contemporary African artists. Real-time bidding, reserve-protected lots, and direct artist provenance."
        path="/auctions"
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Auctions", path: "/auctions" },
        ])}
      />
      <div className="flex items-center gap-3">
        <Gavel className="w-7 h-7 text-[#1E3A8A]" strokeWidth={1.5} />
        <p className="label-eyebrow text-[#1E3A8A]">— Live Auctions</p>
      </div>
      <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tighter font-medium mt-4 text-[#111]">
        The auction floor
      </h1>
      <p className="text-[#555] mt-4 max-w-2xl">
        Bid on original works direct from the studio. Highest bidder at close wins.
        Anti-snipe rule: bids in the final five minutes extend the clock by five minutes.
      </p>

      <div className="mt-10 flex gap-2 border-b border-[#E5E0D8]">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`label-eyebrow px-5 py-3 -mb-px border-b-2 transition-colors ${
              tab === t.key
                ? "border-[#1E3A8A] text-[#1E3A8A]"
                : "border-transparent text-[#555] hover:text-[#111]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {(() => {
          if (loading) {
            return <p className="label-eyebrow text-[#555]">Loading the floor…</p>;
          }
          if (auctions.length === 0) {
            return (
              <p className="font-serif-display text-3xl text-[#555]">
                No {tab === "live" ? "active" : tab} auctions right now.
              </p>
            );
          }
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {auctions.map((a) => (
                <AuctionCard key={a.id} auction={a} />
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
